'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { MarketsFeaturedSkeleton } from '@/components/markets/featured-market-hero/markets-featured-skeleton';
import { MarketRowCardSkeleton } from '@/components/markets/market-row-card-skeleton';
import { ListingDiscoveryActionsSkeleton } from '@/components/shared/listing-discovery-actions-skeleton';
import { Skeleton } from '@/components/ui/app-skeleton';
import { LISTING_INDEX_PAGE_STACK_CLASS } from '@/lib/ui/layout';
import { cn } from '@/lib/ui/utils';

// Route-level Suspense fallback — shown only while the RSC `page.tsx`
// awaits `buildMarketsListingPrefetch`, which is a parallel HTTP burst
// that lands in ~hundreds of ms. After that paint, `<SsrHydrate>` is
// mounted and the SDK cache is hydrated so the live `MarketsPage`
// renders with data on first frame.
//
// Mirrors `MarketsPage`'s above-the-fold layout so the skeleton→content
// swap is shape-stable: the desktop featured-hero row (70% hero + a
// right column), the header + sort-pill chrome, then
// the first row of the responsive market-card grid.

const SORT_PILL_KEYS = ['trending', 'newest', 'ending', 'prize', 'resolved'] as const;
const GRID_CARD_KEYS = ['c-0', 'c-1', 'c-2', 'c-3', 'c-4', 'c-5'] as const;

export default function MarketsLoading(): React.ReactNode {
  return (
    <Suspense fallback={<MarketsLoadingContent showFeaturedHero />}>
      <MarketsLoadingWithParams />
    </Suspense>
  );
}

function MarketsLoadingWithParams(): React.ReactNode {
  const searchParams = useSearchParams();
  const categorySelected = (searchParams.get('category') ?? '')
    .split(',')
    .some((category) => category.trim().length > 0);
  const showFeaturedHero = !categorySelected && searchParams.get('view') !== 'watchlist';

  return <MarketsLoadingContent showFeaturedHero={showFeaturedHero} />;
}

function MarketsLoadingContent({
  showFeaturedHero,
}: {
  showFeaturedHero: boolean;
}): React.ReactNode {
  return (
    <div className={cn('w-full min-w-0', LISTING_INDEX_PAGE_STACK_CLASS)} aria-busy="true">
      {showFeaturedHero && <MarketsFeaturedSkeleton />}

      {/* Header + sort/filter pill bar — mirrors MarketsHeader + SortFilterBar. */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-7 w-40 shrink-0 rounded-item" />
          <ListingDiscoveryActionsSkeleton />
        </div>
        <div className="mt-4 flex items-center gap-2 overflow-hidden md:mt-6">
          {SORT_PILL_KEYS.map((key) => (
            <Skeleton key={key} className="h-9 w-24 shrink-0 rounded-pill" />
          ))}
        </div>
      </div>

      {/* Market-card grid — mirrors the responsive 1/2/3-column feed grid,
          including the IntroWidget banner that occupies row 1 at md+ (full-width
          96px band at md; columns 2–3, full-height at lg) and is hidden on
          mobile, so `grid-flow-row-dense` backfills the same slots as the live
          grid. */}
      <div className="grid grid-flow-row-dense grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {/* IntroWidget placeholder — mirrors intro-widget.tsx's grid placement
            classes (`hidden md:block md:col-span-2 md:row-start-1 md:h-24
            lg:col-start-2 lg:col-span-2 lg:h-full`) exactly so row 1 reflows
            identically. */}
        <Skeleton className="hidden md:block md:col-span-2 md:row-start-1 md:h-24 lg:col-start-2 lg:col-span-2 lg:h-full rounded-card" />
        {GRID_CARD_KEYS.map((key) => (
          <MarketRowCardSkeleton key={key} />
        ))}
      </div>
    </div>
  );
}
